import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { USER_STATUS } from '@/shared/constants/users';
import { formatUnixDateTime } from '@/shared/lib/datetime';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { fetchUserProfile, showUserPhone, type UserProfile } from './api';
import { UserBidsBox } from './UserBidsBox';
import { UserBillingBox } from './UserBillingBox';
import { UserNotificationsBox } from './UserNotificationsBox';
import { isServiceRole } from './profileForm';

const LOYALTY_MODE_LABEL: Record<number, string> = { 0: 'Авто', 1: 'Вручную' };

const formatDiscount = (percent: number | string | undefined) =>
  percent ? `Скидка до ${Number(percent).toFixed(0)}%` : '';

const InfoRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <li className="list-group-item">
    <b>{label}</b>
    <span className="pull-right">{children}</span>
  </li>
);

const PhoneRow = ({ user }: { user: UserProfile }) => {
  const [phone, setPhone] = useState<string | null>(null);
  const reveal = useMutation({
    mutationFn: () => showUserPhone(user.id),
    onSuccess: (res) => res.success === 1 && setPhone(res.phone ?? ''),
  });

  return (
    <InfoRow label="Телефон">
      {phone === null ? (
        <button type="button" className="btn btn-primary btn-sm" disabled={reveal.isPending} onClick={() => reveal.mutate()}>
          Показать телефон
        </button>
      ) : (
        phone
      )}
    </InfoRow>
  );
};

const ProfileSummary = ({ user, isService }: { user: UserProfile; isService: boolean }) => {
  const tariffs = user.tariffs ?? [];
  const level = user.discount_level;
  const serviceDiscount = user.service_discount;

  return (
    <div className="box box-primary">
      <div className="box-body box-profile">
        <h3 className="profile-username text-center">{user.name}</h3>
        <p className="text-muted text-center">
          {(user.roles ?? []).map((role) => role.label || role.value).join(', ')} ·{' '}
          {USER_STATUS[Number(user.status_id)] ?? user.status_id}
        </p>
        <ul className="list-group list-group-unbordered">
          <InfoRow label="Email">{user.email}</InfoRow>
          <PhoneRow user={user} />
          {isService && <InfoRow label="Контактный телефон">{user.contact_phone}</InfoRow>}
          <InfoRow label="Тариф">
            {tariffs.length
              ? tariffs.map((tariff) => (
                  <span key={tariff.name} style={{ display: 'block' }}>
                    {tariff.name}
                    {tariff.expired_time ? `, ${formatUnixDateTime(tariff.expired_time)}` : ''}
                  </span>
                ))
              : '-'}
          </InfoRow>
          <InfoRow label="Баланс">{user.balance}</InfoRow>
          <InfoRow label="Бонусный баланс">{user.bonus_balance}</InfoRow>
          {isService && <InfoRow label="Маркетинговый текст">{user.marketing_text}</InfoRow>}
          {!isService && (
            <InfoRow label="Режим уровня лояльности">{LOYALTY_MODE_LABEL[Number(user.discount_level_mode)]}</InfoRow>
          )}
          {level?.title ? (
            <>
              <InfoRow label="Уровень лояльности">{level.title}</InfoRow>
              <InfoRow label="Скидка">{formatDiscount(level.max_discount_percent)}</InfoRow>
            </>
          ) : (
            <>
              <InfoRow label="Скидочная программа">{serviceDiscount?.name}</InfoRow>
              <InfoRow label="Скидка">{formatDiscount(serviceDiscount?.max_discount_percent)}</InfoRow>
            </>
          )}
        </ul>
        <Link to={`/users/edit/${user.id}`} className="btn btn-success btn-block">
          Редактировать
        </Link>
      </div>
    </div>
  );
};

export const UserDetailPage = () => {
  const { id = '' } = useParams();
  const { data: user, isLoading, error } = useQuery({
    queryKey: ['user', id, 'view'],
    queryFn: () => fetchUserProfile(id),
  });
  const isService = isServiceRole((user?.roles ?? []).map((role) => role.value));

  return (
    <>
      <section className="content-header">
        <h1>Профиль пользователя</h1>
      </section>
      <section className="content">
        <QueryStatus isLoading={isLoading} error={error} errorText="Не удалось загрузить профиль" />
        {user && (
          <>
            <div className="row">
              <div className="col-md-4">
                <ProfileSummary user={user} isService={isService} />
              </div>
              <div className="col-md-4">
                <UserBillingBox userId={id} operation="refill" />
              </div>
              <div className="col-md-4">
                <UserBillingBox userId={id} operation="spending" />
              </div>
            </div>
            <div className="row">
              <div className="col-md-12">
                <UserNotificationsBox userId={id} />
                <UserBidsBox userId={id} isService={isService} />
              </div>
            </div>
          </>
        )}
      </section>
    </>
  );
};
